import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import { Visibility } from "@material-ui/icons";
import Close from "@material-ui/icons/Close";
import CloudDownload from "@material-ui/icons/CloudDownload";
import { CircularProgress } from "@mui/material";
import CustomPopover, {
  CustomPopoverRef,
} from "components/UI/CustomPopover/CustomPopover";
import PopoverButton from "components/UI/CustomPopover/PopoverButton";
import axios from "axios";
import Button from "components/Forms/CustomButtons/ButtonTS";
import PaginatedTable from "components/Tables/ReactTable/ManualPaginatedTable";
import { TabContent, TabSystem } from "components/UI/TabSystem";
import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DocumentAttachment } from "services/input-core/document-attachment/@types";
import { useGetPaginatedDocumentAttachments } from "services/input-core/document-attachment/get-paginated-document-attachments";
import { ActivityItemWorker } from "services/input-core/work-items/@types";
import { ContainerActions } from "./styles";
import { toast } from "react-toastify";
import { useBlobToken } from "services/blob/get-token";
import { useActivityItemContext } from "store/activityItem";

interface UploadModalProps {
  modalOpen: boolean;
  fieldglassId?: string;
  docs?: any[];
  zIdentifier?: any;
  onClose: () => void;
}

interface DocumentFormat {
  name: string;
  uploadedAt: string;
  fromApi: boolean;
  doc: any;
}

async function openImageInNewTab(
  fileUri: string,
  doc: DocumentAttachment,
  notificationCallback: () => void,
) {
  if (!fileUri) return;

  if (fileUri) {
    let metadata = {};
    let fileType = "";

    notificationCallback();

    const { data } = await axios.get(fileUri, {
      responseType: "blob",
    });

    const getFileExtension = (uri: string): string => {
      const cleanUri = uri.split("?")[0];
      return cleanUri.split(".").pop()?.toLowerCase() ?? "";
    };

    const extension = getFileExtension(fileUri);

    if (extension === "pdf") {
      fileType = "application/pdf";
    } else if (["png", "jpg", "jpeg"].includes(extension)) {
      fileType = `image/${extension}`;
    }

    if (!doc?.mimeType) {
      metadata = {
        type: fileType,
        disposition: "inline",
      };
    } else {
      metadata = {
        type: doc?.mimeType,
        disposition: "inline",
      };
    }

    const file = new File([data], "file", metadata);

    const fileURL = URL.createObjectURL(file);

    window.open(fileURL);
  }
}

const ActionButtons: React.FC<{
  fileUri: string;
  currentFile?: any;
  doc?: DocumentAttachment;
  disabled?: boolean;
  notificationCallback: () => void;
}> = ({ notificationCallback, fileUri, doc }) => {
  const { t } = useTranslation("supplier-list");
  const { mutate: blobToken, isLoading: isLoadingBlobToken } = useBlobToken();

  const customPopoverRef = useRef<CustomPopoverRef>(null);

  const closePopoverFromParent = () => {
    if (customPopoverRef.current) {
      customPopoverRef.current.closePopover();
    }
  };

  return (
    <ContainerActions>
      <CustomPopover ref={customPopoverRef} identifier={`check-documents`}>
        <PopoverButton
          disabled={!fileUri || isLoadingBlobToken}
          color="primary"
          onClick={() => {
            closePopoverFromParent();
            blobToken(fileUri, {
              onSuccess: () => {
                openImageInNewTab(
                  doc.uri ?? doc.url,
                  doc,
                  notificationCallback,
                );
              },
              onError: () => {
                toast.error(t("get-blob-token-error", { ns: "list-errors" }));
              },
            });
          }}
        >
          <Visibility />
          {t("preview")}
        </PopoverButton>
        <PopoverButton
          disabled={!fileUri || isLoadingBlobToken}
          color="primary"
          onClick={() => {
            closePopoverFromParent();
            blobToken(fileUri, {
              onSuccess: () => {
                window.open(
                  doc?.uri ?? doc?.url,
                  "_blank",
                  "noopener,noreferrer",
                );
              },
              onError: () => {
                toast.error(t("get-blob-token-error", { ns: "list-errors" }));
              },
            });
          }}
        >
          <CloudDownload />
          {t("download")}
        </PopoverButton>
      </CustomPopover>
    </ContainerActions>
  );
};

const CheckDocumentsModal: React.FC<UploadModalProps> = ({
  modalOpen,
  onClose,
  docs,
  zIdentifier,
  fieldglassId,
}) => {
  const { t } = useTranslation(["supplier-list", "list-messages"]);

  const { currentActivityItem } = useActivityItemContext((state) => ({
    currentActivityItem: state.currentActivity,
  }));

  const columns: any = [
    {
      Header: t("supplier-table-name"),
      header: t("supplier-table-name"),
      accessor: "name",
      accessorKey: "name",
    },
    {
      Header: t("supplier-table-upload-by"),
      header: t("supplier-table-upload-by"),
      accessor: "uploadedBy",
      accessorKey: "uploadedBy",
    },
    {
      Header: t("supplier-table-upload"),
      header: t("supplier-table-upload"),
      accessor: "uploadedAt",
      accessorKey: "uploadedAt",
    },
    {
      Header: t("supplier-table-action"),
      header: t("supplier-table-action"),
      accessor: "actions",
      accessorKey: "actions",
      enableColumnFilter: false,
      cell: ({ row }: any) => {
        return (
          <ActionButtons
            fileUri={row.original.doc.uri || row.original.doc.url}
            doc={row.original.doc}
            key={row.original.doc._id}
            notificationCallback={() =>
              toast.info(t("info-open-preview", { ns: "list-messages" }))
            }
          />
        );
      },
      Cell: ({ row }: any) => {
        return (
          <ActionButtons
            fileUri={row.original.doc.uri || row.original.doc.url}
            doc={row.original.doc}
            key={row.original.doc._id}
            notificationCallback={() =>
              toast.info(t("info-open-preview", { ns: "list-messages" }))
            }
          />
        );
      },
    },
  ];

  const [tableWithHytoryDocs, setTableWithHytoryDocs] = React.useState<any>({
    pageIndex: 0,
    pageSize: 5,
  });

  const [tableWithCurrentDocs, setTableWithCurrentDocs] = React.useState<any>({
    pageIndex: 0,
    pageSize: 5,
  });

  const {
    data,
    isLoading,
    remove: removeCurrentDocuments,
  } = useGetPaginatedDocumentAttachments({
    zID: currentActivityItem?.zIdentifier || zIdentifier,
    fieldglassId: currentActivityItem?.fieldglassId || fieldglassId,
    active: true,
    params: {
      page: tableWithCurrentDocs.pageIndex + 1,
      limit: tableWithCurrentDocs.pageSize,
    },
  });

  const {
    data: historyDocumentsFromActivity,
    isLoading: isLoadingHistoryDocumentsFromActivity,
    remove: removeHistoryDocuments,
  } = useGetPaginatedDocumentAttachments({
    zID: currentActivityItem?.zIdentifier || zIdentifier,
    fieldglassId: currentActivityItem?.fieldglassId || fieldglassId,
    active: false,
    params: {
      page: tableWithHytoryDocs.pageIndex + 1,
      limit: tableWithHytoryDocs.pageSize,
    },
  });

  const handleOnCloseModal = useCallback(() => {
    removeHistoryDocuments();
    removeCurrentDocuments();
    onClose();
  }, []);

  let dataFormatted: DocumentFormat[] = [];
  let historyDataFormatted: DocumentFormat[] = [];

  const tabs = useMemo(
    () => [
      {
        id: "docs1",
        title: t("current"),
        hasCounter: docs?.length ?? data?.totalDocs,
      },
      {
        id: "docs2",
        title: t("history"),
        hasCounter: historyDocumentsFromActivity?.totalDocs,
      },
    ],
    [historyDocumentsFromActivity?.totalDocs, data?.totalDocs],
  );

  function formatDocument(doc: any) {
    return {
      name: doc.name,
      uploadedBy: `${doc?.createdBy?.name} (${doc?.createdBy?.email})`,
      uploadedAt: new Date(doc.createdAt).toLocaleString(),
      fromApi: true,
      doc: doc,
    };
  }

  function formatDocumentStack(doc: any) {
    return {
      name: doc.name,
      uploadedBy: `${doc?.userId} (${doc?.userEmail})`,
      uploadedAt: new Date(doc.createdAt).toLocaleString(),
      fromApi: true,
      doc: doc,
    };
  }

  dataFormatted =
    docs?.map((doc) => formatDocumentStack(doc)) ??
    (data?.docs?.map((doc) => formatDocument(doc)) || []);

  historyDataFormatted =
    historyDocumentsFromActivity?.docs?.map((doc) => formatDocument(doc)) || [];

  const isLoadingDocs = isLoading || isLoadingHistoryDocumentsFromActivity;

  const isLoadingWorkItemDocumentsData = isLoadingDocs;

  return (
    <Dialog
      open={modalOpen}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      onClose={() => {
        handleOnCloseModal();
      }}
      disableBackdropClick
    >
      <DialogTitle>
        {t("document-list")}
        <Button
          justIcon
          simple
          color="tumblr"
          round
          size="sm"
          style={{
            position: "absolute",
            right: "10px",
            top: "10px",
          }}
          onClick={() => {
            handleOnCloseModal();
          }}
        >
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent>
        <TabSystem tabs={tabs}>
          <TabContent value="docs1">
            {isLoadingWorkItemDocumentsData ? (
              <CircularProgress />
            ) : dataFormatted?.length === 0 ? (
              <div style={{ textAlign: "center" }}>
                <h4> {t("no-documents-found")}</h4>
              </div>
            ) : (
              <PaginatedTable
                hasHeader={true}
                columns={columns}
                data={dataFormatted}
                pageCount={data?.totalPages}
                setTableState={setTableWithCurrentDocs}
                tableState={tableWithCurrentDocs}
              />
            )}
          </TabContent>
          <TabContent value="docs2">
            {isLoadingWorkItemDocumentsData ? (
              <CircularProgress />
            ) : historyDataFormatted?.length === 0 ? (
              <div style={{ textAlign: "center" }}>
                <h4> {t("no-documents-found")}</h4>
              </div>
            ) : (
              <PaginatedTable
                hasHeader={true}
                columns={columns}
                data={historyDataFormatted}
                pageCount={historyDocumentsFromActivity?.totalPages}
                setTableState={setTableWithHytoryDocs}
                tableState={tableWithHytoryDocs}
              />
            )}
          </TabContent>
        </TabSystem>
      </DialogContent>
    </Dialog>
  );
};

export default CheckDocumentsModal;
